import { lockScroll, unlockScroll, onDocumentLoaded } from '@theme/utilities';

class TrProductsDropdown extends HTMLElement {
  #controller;
  #panel;
  #toggle;
  #parent;
  #tapLayout;
  #tapPointer = false;
  #closeTimer;
  #suppressed = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#panel = this.querySelector('.tr-products-dropdown__panel');
    this.#toggle = this.querySelector('button');
    this.#parent = this.querySelector(':scope > a');
    this.#tapLayout = matchMedia('(max-width: 1024px), (hover: none), (pointer: coarse)');
    this.#syncParentMode();
    this.#tapLayout.addEventListener('change', () => { this.#close(); this.#syncParentMode(); }, { signal });
    this.addEventListener('pointerdown', (event) => {
      this.#tapPointer = event.pointerType === 'touch' || event.pointerType === 'pen';
      this.#syncParentMode();
    }, { capture: true, signal });
    this.addEventListener('pointerenter', (event) => {
      this.#tapPointer = event.pointerType === 'touch' || event.pointerType === 'pen';
      this.#syncParentMode();
      if (!this.#isTapMode()) { this.#suppressed = false; this.#open(); }
    }, { signal });
    this.addEventListener('pointerleave', this.#deferClose, { signal });
    this.#panel.addEventListener('pointerenter', () => clearTimeout(this.#closeTimer), { signal });
    this.#panel.addEventListener('pointerleave', this.#deferClose, { signal });
    this.addEventListener('focusin', (event) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible') && !this.#tapLayout.matches) {
        this.#tapPointer = false;
        this.#syncParentMode();
      }
      // A touch focus must not open the menu before the click handler toggles it.
      if (this.#isTapMode()) return;
      if (!this.contains(event.relatedTarget)) this.#suppressed = false;
      if (!this.#suppressed) this.#open();
    }, { signal });
    this.addEventListener('focusout', (event) => { if (!this.contains(event.relatedTarget)) this.#close(); }, { signal });
    this.#toggle.addEventListener('click', this.#togglePanel, { signal });
    this.#parent.addEventListener('click', (event) => {
      if (!this.#isTapMode()) return;
      event.preventDefault();
      event.stopPropagation();
      this.#togglePanel();
    }, { capture: true, signal });
    this.addEventListener('keydown', this.#keyDown, { signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) { this.#suppressed = true; this.#close(); }
    }, { signal });
    window.addEventListener('resize', this.#close, { signal });
    document.addEventListener('scroll', this.#place, { capture: true, passive: true, signal });
  }

  disconnectedCallback() {
    this.#close();
    this.#controller.abort();
    clearTimeout(this.#closeTimer);
  }

  #open = () => {
    clearTimeout(this.#closeTimer);
    if (this.#suppressed || !this.checkVisibility()) return;
    this.#panel.hidden = false;
    if (this.#panel.showPopover && !this.#panel.matches(':popover-open')) this.#panel.showPopover();
    this.#toggle.ariaExpanded = 'true';
    this.#syncParentMode();
    this.#place();
  };

  #close = () => {
    if (this.#panel?.matches(':popover-open')) this.#panel.hidePopover();
    if (this.#panel) this.#panel.hidden = true;
    if (this.#toggle) this.#toggle.ariaExpanded = 'false';
    this.#syncParentMode();
  };

  #isTapMode = () => this.#tapLayout.matches || this.#tapPointer;

  #syncParentMode = () => {
    if (!this.#parent) return;
    if (this.#isTapMode()) {
      this.#parent.setAttribute('role', 'button');
      this.#parent.setAttribute('aria-controls', this.#toggle.getAttribute('aria-controls'));
      this.#parent.ariaExpanded = this.#toggle.ariaExpanded;
    } else {
      this.#parent.removeAttribute('role');
      this.#parent.removeAttribute('aria-controls');
      this.#parent.removeAttribute('aria-expanded');
    }
  };

  #togglePanel = () => {
    if (this.#toggle.ariaExpanded === 'true') this.#close();
    else { this.#suppressed = false; this.#open(); }
  };

  #deferClose = () => {
    if (this.#isTapMode()) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (!this.matches(':hover') && !this.#panel.matches(':hover') && !this.contains(document.activeElement)) this.#close();
    }, 150);
  };

  #place = () => {
    if (this.#toggle.ariaExpanded !== 'true') return;
    const anchor = this.getBoundingClientRect();
    const width = this.#panel.offsetWidth;
    this.#panel.style.left = `${Math.max(16, Math.min(anchor.left, innerWidth - width - 16))}px`;
    // Keep the panel clear of the full navigation row with a compact 4px offset.
    this.#panel.style.top = `${anchor.bottom - 8}px`;
  };

  #keyDown = (event) => {
    if (event.key === ' ' && document.activeElement === this.#parent && this.#isTapMode()) {
      event.preventDefault();
      event.stopPropagation();
      this.#togglePanel();
      return;
    }
    const links = [...this.#panel.querySelectorAll('a')];
    const index = links.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.#suppressed = true;
      this.querySelector('a').focus();
      this.#close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      this.#suppressed = false;
      this.#open();
      const next = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
      links[next]?.focus();
    } else if (index !== -1 && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      links[event.key === 'Home' ? 0 : links.length - 1]?.focus();
    }
  };
}

class TrHeaderDrawer extends HTMLElement {
  #controller;
  #dialog;
  #trigger;
  #products;
  #animation;
  #opening;
  #closeTimer;
  #socialObserver;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#dialog = this.querySelector('dialog');
    this.#trigger = this.querySelector('.tr-header-drawer__trigger');
    this.#products = this.querySelector('.tr-header-drawer__products');
    this.#trigger.addEventListener('click', this.#open, { signal });
    this.querySelector('.tr-header-drawer__close').addEventListener('click', this.#close, { signal });
    this.#dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.#close(); }, { signal });
    this.#dialog.addEventListener('close', this.#afterClose, { signal });
    this.#dialog.addEventListener('keydown', this.#trapTab, { signal });
    this.#dialog.addEventListener('click', (event) => {
      const bounds = this.#dialog.getBoundingClientRect();
      if (event.target === this.#dialog && (event.clientX > bounds.right || event.clientX < bounds.left || event.clientY < bounds.top || event.clientY > bounds.bottom)) this.#close();
    }, { signal });
    this.#products?.querySelector('summary').addEventListener('click', this.#toggleProducts, { signal });
    this.#products?.querySelector('summary').setAttribute('aria-expanded', String(this.#products.open));
    window.addEventListener('resize', () => { if (this.#dialog.open && !this.#trigger.checkVisibility()) this.#finishClose(); }, { signal });
    document.addEventListener('shopify:section:load', this.#syncSocials, { signal });
    this.#syncSocials();
    onDocumentLoaded(() => { if (!signal.aborted) this.#syncSocials(); });
  }

  disconnectedCallback() {
    clearTimeout(this.#closeTimer);
    this.#animation?.cancel();
    this.#finishClose();
    this.#controller.abort();
    this.#socialObserver?.disconnect();
  }

  #open = () => {
    clearTimeout(this.#closeTimer);
    delete this.#dialog.dataset.closing;
    this.#syncSocials();
    if (this.#products) {
      this.#animation?.cancel();
      this.#animation = null;
      this.#opening = true;
      this.#products.open = true;
      this.#products.dataset.expanded = 'true';
      this.#products.style.overflow = '';
      this.#products.querySelector('summary').ariaExpanded = 'true';
    }
    if (!this.#dialog.open) this.#dialog.showModal();
    lockScroll(this.#dialog);
    this.#trigger.ariaExpanded = 'true';
  };

  #close = () => {
    if (!this.#dialog.open || this.#dialog.hasAttribute('data-closing')) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) this.#finishClose();
    else {
      this.#dialog.dataset.closing = '';
      this.#closeTimer = setTimeout(this.#finishClose, 210);
    }
  };

  #finishClose = () => {
    if (this.#dialog?.open) this.#dialog.close();
    if (this.#dialog) delete this.#dialog.dataset.closing;
    this.#afterClose();
  };

  #afterClose = () => {
    if (!this.#dialog) return;
    unlockScroll(this.#dialog);
    this.#trigger.ariaExpanded = 'false';
    if (this.isConnected && this.#trigger.checkVisibility()) this.#trigger.focus({ preventScroll: true });
  };

  #trapTab = (event) => {
    if (event.key !== 'Tab') return;
    const items = [...this.#dialog.querySelectorAll('a[href], button:not(:disabled), summary')]
      .filter(item => item.checkVisibility({ checkVisibilityCSS: true }));
    const first = items[0];
    const last = items.at(-1);
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.#dialog)) {
      event.preventDefault();
      last?.focus();
    }
  };

  #toggleProducts = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const details = this.#products;
    const opening = this.#animation ? !this.#opening : !details.open;
    const height = details.getBoundingClientRect().height;
    this.#animation?.cancel();
    this.#animation = null;
    this.#opening = opening;
    details.dataset.expanded = String(opening);
    const summary = details.querySelector('summary');
    summary.ariaExpanded = String(opening);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { details.open = opening; details.style.overflow = ''; return; }
    details.open = true;
    details.style.overflow = 'hidden';
    const end = summary.offsetHeight + (opening ? details.querySelector('ul').offsetHeight : 0);
    this.#animation = details.animate({ height: [`${height}px`, `${end}px`] }, { duration: 220, easing: 'ease' });
    this.#animation.onfinish = () => { details.open = opening; details.style.overflow = ''; this.#animation = null; };
  };

  #syncSocials = () => {
    const footer = document.querySelector('.tr-footer-pink__social');
    if (!footer) {
      this.#socialObserver?.disconnect();
      this.#socialObserver = new MutationObserver(() => {
        if (document.querySelector('.tr-footer-pink__social')) this.#syncSocials();
      });
      this.#socialObserver.observe(document.body, { childList: true, subtree: true });
      return;
    }
    const copy = () => {
      const links = [...footer.querySelectorAll('a')].map(link => {
        const clone = link.cloneNode(true);
        clone.target = '_blank';
        clone.rel = 'noopener noreferrer';
        return clone;
      });
      this.querySelector('.tr-header-drawer__social').replaceChildren(...links);
    };
    copy();
    this.#socialObserver?.disconnect();
    this.#socialObserver = new MutationObserver(copy);
    this.#socialObserver.observe(footer, { subtree: true, childList: true, attributes: true, attributeFilter: ['href', 'target', 'rel'] });
  };
}

if (!customElements.get('tr-products-dropdown')) customElements.define('tr-products-dropdown', TrProductsDropdown);
if (!customElements.get('tr-header-drawer')) customElements.define('tr-header-drawer', TrHeaderDrawer);
